import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api/api";
import Spinner from "../components/common/Spinner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const LOGO_FALLBACK = "/uploads/imagens/logo.png"; 

export default function PublicationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [publication, setPublication] = useState(null);
  const [allPublications, setAllPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [showToast, setShowToast] = useState(false);

  // Estados para a lógica de Drag (Arrastar)
  const [isDragging, setIsDragging] = useState(false);
  const [coords, setCoords] = useState({ startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = publication ? encodeURIComponent(`Confira: ${publication.title}`) : '';

  const getImageUrl = (item) => {
    if (!item) return LOGO_FALLBACK;
    const rawPath = item.media?.url || item.media_url || item.file_url || item.url || item.image || item.thumb;
    if (!rawPath) return LOGO_FALLBACK;
    if (String(rawPath).startsWith("http")) return String(rawPath).replace(/^http:\/\//i, "https://");
    const baseUrl = api.defaults.baseURL?.replace(/\/api\/?$/, "") || "";
    return `${baseUrl}${rawPath.startsWith("/") ? "" : "/"}${rawPath}`;
  };

  const fetchPublication = useCallback(async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) setLoading(true);
      const res = await api.get("/admin/publications");
      const data = res.data?.data || [];
      setAllPublications(data);
      const found = data.find((p) => String(p.id) === String(id));
      if (!found) { if (isInitialLoad) navigate("/404"); return; }
      setPublication(found);
    } catch (error) { if (isInitialLoad) navigate("/404"); } finally { if (isInitialLoad) setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { if (id) fetchPublication(true); }, [id, fetchPublication]);

  // --- LÓGICA DE DRAG (Mãozinha) ---
  const handleMouseDown = (e) => {
    if (zoom <= 100) return;
    setIsDragging(true);
    setCoords({
      startX: e.pageX - scrollRef.current.offsetLeft,
      startY: e.pageY - scrollRef.current.offsetTop,
      scrollLeft: scrollRef.current.scrollLeft,
      scrollTop: scrollRef.current.scrollTop
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const y = e.pageY - scrollRef.current.offsetTop;
    const walkX = (x - coords.startX) * 1.5;
    const walkY = (y - coords.startY) * 1.5;
    scrollRef.current.scrollLeft = coords.scrollLeft - walkX;
    scrollRef.current.scrollTop = coords.scrollTop - walkY;
  };

  const stopDragging = () => setIsDragging(false);

  // --- UTILITÁRIOS: IMPRIMIR E BAIXAR ---
  const handlePrint = () => {
    const url = getImageUrl(publication);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
  };

  const handleDownload = async () => {
    const url = getImageUrl(publication);
    const link = document.createElement("a");
    link.href = url;
    link.download = publication.title || "arquivo";
    link.target = "_blank";
    link.click();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleImgError = (e) => {
    e.target.onerror = null;
    e.target.src = LOGO_FALLBACK;
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen bg-white"><Spinner /></div>;
  if (!publication) return null;

  const finalUrl = getImageUrl(publication);
  const isPdf = publication.type === "pdf" || finalUrl?.toLowerCase().endsWith(".pdf");

  return (
    <div className="bg-white min-h-screen flex flex-col font-sans text-slate-800">
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" />

      <main className="max-w-[1000px] mx-auto px-4 pt-11 flex-grow">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#053b50] leading-tight mb-6">{publication.title}</h1>

        {/* HEADER SOCIAIS COM FACEBOOK */}
        <div className="flex flex-col md:flex-row md:items-center justify-between py-6 border-y border-gray-100 mb-10 gap-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold">{publication.author_name?.charAt(0) || "A"}</div>
            <div className="flex flex-col text-sm">
              <span className="font-bold text-gray-900">Autor: {publication.author.name || "Admin"}</span>
              <span className="text-gray-500">Publicado em {publication.created_at && format(new Date(publication.created_at), "dd/MM/yyyy", { locale: ptBR })}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-2">
              <a href={`https://api.whatsapp.com/send?text=${shareText}%20${currentUrl}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:scale-110 transition shadow-sm"><i className="fab fa-whatsapp" /></a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:scale-110 transition shadow-sm"><i className="fab fa-facebook-f text-sm" /></a>
              <button onClick={handleCopyLink} className="w-9 h-9 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-gray-200 transition shadow-sm"><i className="fas fa-link text-xs" /></button>
            </div>
          </div>
        </div>

        {/* VISUALIZADOR COM SCROLL E DRAG FUNCIONAIS */}
        <div className="relative mb-4">
          <div 
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={stopDragging}
            onMouseUp={stopDragging}
            onMouseMove={handleMouseMove}
            className={`w-full bg-slate-900 rounded-2xl overflow-auto shadow-xl border border-gray-200 
              ${zoom > 100 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'}`}
            style={{ height: isPdf ? "650px" : "auto", maxHeight: "80vh" }}
          >
            <div 
              style={{ 
                width: `${zoom}%`, 
                minWidth: '100%',
                margin: '0 auto',
                transition: isDragging ? 'none' : 'width 0.2s ease-out',
                pointerEvents: isDragging ? 'none' : 'auto' 
              }}
            >
              {isPdf ? (
                <iframe 
                  src={`${finalUrl}#toolbar=0&navpanes=0&view=FitH`} 
                  className="w-full h-[650px] border-none" 
                  style={{ pointerEvents: zoom > 100 ? 'none' : 'auto' }}
                  title={publication.title} 
                />
              ) : (
                <img 
                  src={finalUrl} 
                  alt={publication.title} 
                  className="w-full h-auto object-contain mx-auto" 
                  style={{ pointerEvents: zoom > 100 ? 'none' : 'auto' }}
                />
              )}
            </div>
          </div>
        </div>

        {/* TOOLBAR INTEGRADA */}
        <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-2xl mb-10 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2">
            <button onClick={() => setZoom(prev => Math.max(prev - 25, 100))} className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg text-gray-500 hover:bg-gray-100">-</button>
            <span className="text-sm font-bold text-gray-600 w-12 text-center">{zoom}%</span>
            <button onClick={() => setZoom(prev => Math.min(prev + 25, 400))} className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg text-gray-500 hover:bg-gray-100">+</button>
            <button onClick={() => setZoom(100)} className="ml-2 text-[10px] font-bold text-indigo-600 uppercase">Reset</button>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50"><i className="fas fa-print" /> Imprimir</button>
            <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 bg-[#053b50] rounded-xl text-sm font-bold text-white hover:opacity-90"><i className="fas fa-download" /> Baixar</button>
          </div>
        </div>

        <div className="article-body mb-20 prose prose-lg max-w-none text-gray-700">
           <div dangerouslySetInnerHTML={{ __html: publication.content }} />
        </div>

        {/* MAIS PUBLICAÇÕES COM O BOTÃO SOLICITADO */}
        <hr className="border-gray-100 mb-10" />
        <div className="mb-20">
          <div className="flex justify-between items-end mb-8">
            <h2 className="text-2xl font-bold text-slate-800">Mais Publicações</h2>
            <Link to="/galeria" className="text-indigo-600 text-sm font-bold hover:underline">Ver todas →</Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {allPublications.filter(p => String(p.id) !== String(id)).slice(0, 3).map((art) => (
              <Link key={art.id} to={`/publicacoes/${art.id}`} className="group bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                <div className="h-40 bg-gray-50 overflow-hidden">
                  <img 
                    src={getImageUrl(art)} 
                    alt={art.title}
                    onError={handleImgError}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-slate-800 line-clamp-2 group-hover:text-indigo-600 transition-colors">{art.title}</h3>
                  <span className="text-[10px] text-gray-400 mt-2 block uppercase">{art.created_at && format(new Date(art.created_at), "dd/MM/yyyy")}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      {/* TOAST DE FEEDBACK */}
      <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[9999] transition-all duration-500 transform ${showToast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
        <div className="bg-[#053b50] text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 border border-indigo-400/30">
          <div className="bg-green-500 rounded-full p-1"><i className="fas fa-check text-[10px]" /></div>
          <span className="text-sm font-bold">Link copiado com sucesso!</span>
        </div>
      </div>

      {/* FOOTER COM ANO AUTOMÁTICO */}
      <footer className="bg-gray-50 border-t border-gray-100 py-10 text-center text-sm text-gray-400 mt-auto">
        © {new Date().getFullYear()} Imagens da Face. Todos os direitos reservados.
      </footer>
    </div>
  );
}